import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useNavigate, useParams } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { useState } from 'react';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { useGlobals } from 'src/shared/contexts/appContext';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceMissionModals } from './invoiceMissionModals';
import { InvoiceMissionModalKeys, useInvoiceMissionModalState } from './useInvoiceMissionModalState';
import { InvoiceMissionReminderButton } from './invoiceMissionReminderButton';
import { InvoiceMissionApproveButtons } from './invoiceMissionApproveButtons';
import { InvoiceMissionInvoicesGeneratingIndicator } from './invoiceMissionInvoicesGeneratingIndicator';
import { convertSendingTypeIntToString } from './invoiceMissionSendingTypeConverter';

type SendingSessionSectionBtnHandlerProps = {
  loading: boolean;
  invoiceMission: InvoiceMissionResponse;
  refetchMission: (refetch: boolean) => void;
  onDeleteReminderSending?: () => void;
};

export const SendingSessionSectionBtnHandler = (props: SendingSessionSectionBtnHandlerProps) => {
  const { loading, invoiceMission, refetchMission, onDeleteReminderSending } = props;
  const { t } = useTranslation();
  const { sendingType } = useParams<{ sendingType: string }>();
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(true);
  const parsedSendingType = Number(sendingType);

  const modalState = useInvoiceMissionModalState();

  const mainData = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.invoice);
  const reminder1Data = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.reminder1);
  const reminder2Data = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.reminder2);

  const { currentUser } = useGlobals();

  const canDelete = getPermission(currentUser, PermissionEnum.InvoiceDelete);

  const [openDeleteSendingDialog, setOpenDeleteSendingDialog] = useState<boolean>(false);

  const isMounted = useIsMounted();
  const navigate = useNavigate();

  // Tar bort ett påminnelseutskick (påminnelse 1/2). Efteråt: gå till Alla-fliken (påminnelsens data är borta).
  const onHandleDeleteSending = async (id: number) => {
    try {
      await invoiceMissionService.deleteInvoiceMissionSending(id);
      if (isMounted()) {
        toast.success(t('invoice:invoiceMissionPage.deleteReminderSendingSuccess') as string);
        navigate(`${RoutesEnum.InvoiceMission}/${invoiceMission.invoiceMission_id}/0`);
      }
    } catch (e) {
      toast.error(t('invoice:invoiceMissionPage.deleteReminderSendingError') as string);
    } finally {
      if (isMounted()) {
        setOpenDeleteSendingDialog(false);
      }
    }
  };

  const sendingTypeInt = sendingType ? Number(sendingType) : InvoiceSendingTypeIntegerEnum.NotSet;
  const isReminderTab = sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder1 || sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder2;

  const reminderSendingId = isReminderTab
    ? invoiceMission?.dataList?.find((d) => d.sendingType === convertSendingTypeIntToString(sendingTypeInt))?.approval?.id
    : undefined;

  const reminderNumber = sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder2 ? 2 : 1;

  return (
    <>
      <Grid container direction='row' alignItems='center'>
        {parsedSendingType === InvoiceSendingTypeIntegerEnum.Main && mainData?.approval.approvalRequired && !mainData.approval.approvedAt ? (
          <Grid item sx={{ display: 'flex', gap: 2 }}>
            <InvoiceMissionApproveButtons
              invoiceMissionId={invoiceMission.invoiceMission_id}
              sendingType={parsedSendingType}
              refetchMission={refetchMission}
            />
          </Grid>
        ) : (
          <Grid item sx={{ display: 'flex', gap: 2 }}>
            {/* Knapp för att skicka huvudutskick */}
            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Main && invoiceMission.data.totalCount > 0 && (
              <>
                {isGeneratingInvoices && mainData?.approval.createStartedAt && !mainData?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <Button
                    size='medium'
                    variant='outlined'
                    disabled={loading}
                    onClick={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                  >
                    {t('invoice:invoiceMissionPage.sendSending')}
                  </Button>
                )}
              </>
            )}

            {/* Knapp för att skicka påminnelse 1 */}
            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder1 && (
              <>
                {isGeneratingInvoices && reminder1Data?.approval.createStartedAt && !reminder1Data?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <InvoiceMissionReminderButton
                    reminder={1}
                    prevoiusSendingHasBeenSent={mainData?.totalCountSent ?? 0}
                    totalCount={reminder1Data?.totalCount || 0}
                    totalCountSent={reminder1Data?.totalCountSent || 0}
                    disableCreate={!reminder1Data?.isTemplateSendingValid}
                    approvalRequired={reminder1Data?.approval.approvalRequired ?? true}
                    isApproved={Boolean(reminder1Data?.approval.approvedAt)}
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    loading={loading}
                    setOpenDeliveryMethodModal={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                    setOpenCreateInvoiceReminderModal={() => modalState.setModal(InvoiceMissionModalKeys.CreateReminder, true)}
                    refetchMission={refetchMission}
                    invoiceMission={invoiceMission}
                    sendingType={sendingType}
                    onDeleteReminderSending={canDelete ? () => setOpenDeleteSendingDialog(true) : undefined}
                  />
                )}

                {/* Backa påminnelse 1: syns bara när påminnelsen faktiskt är skapad (approval-id finns).
                {!!reminder1Data?.approval?.id && !!reminder1Data?.approval?.deniedAt && onDeleteReminderSending && (
                  <Button size='medium' variant='outlined' color='error' disabled={loading} onClick={onDeleteReminderSending}>
                    {t('invoice:invoiceMissionPage.deleteReminderSending', { number: 1 })}
                  </Button>
                )}
                  */}
              </>
            )}

            {/* Knapp för att skicka påminnelse 2 */}
            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder2 && (
              <>
                {isGeneratingInvoices && reminder2Data?.approval.createStartedAt && !reminder2Data?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <InvoiceMissionReminderButton
                    reminder={2}
                    prevoiusSendingHasBeenSent={reminder1Data?.totalCountSent ?? 0}
                    totalCount={reminder2Data?.totalCount || 0}
                    totalCountSent={reminder2Data?.totalCountSent || 0}
                    disableCreate={!reminder2Data?.isTemplateSendingValid}
                    approvalRequired={reminder2Data?.approval.approvalRequired ?? true}
                    isApproved={Boolean(reminder2Data?.approval.approvedAt)}
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    loading={loading}
                    setOpenDeliveryMethodModal={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                    setOpenCreateInvoiceReminderModal={() => modalState.setModal(InvoiceMissionModalKeys.CreateReminder, true)}
                    refetchMission={refetchMission}
                    invoiceMission={invoiceMission}
                    sendingType={sendingType}
                    onDeleteReminderSending={canDelete ? () => setOpenDeleteSendingDialog(true) : undefined}
                  />
                )}

                {/* Backa påminnelse 2: syns bara när påminnelsen faktiskt är skapad (approval-id finns). 
                {!!reminder2Data?.approval?.id && !!reminder2Data?.approval?.deniedAt && onDeleteReminderSending && (
                  <Button size='medium' variant='outlined' color='error' disabled={loading} onClick={onDeleteReminderSending}>
                    {t('invoice:invoiceMissionPage.deleteReminderSending', { number: 2 })}
                  </Button>
                )}
                  */}
              </>
            )}
          </Grid>
        )}
      </Grid>

      {invoiceMission && !!reminderSendingId && (
        <DeleteConfirmationDialog
          title={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmTitle', { number: reminderNumber })}
          value={reminderSendingId.toString()}
          modaltext={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmText', { number: reminderNumber })}
          confirmButtonText={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmButton')}
          confirmButtonColor='error'
          onClose={(value) => {
            if (value) {
              onHandleDeleteSending(Number(value.id));
            } else {
              setOpenDeleteSendingDialog(false);
            }
          }}
          open={openDeleteSendingDialog}
        />
      )}

      <InvoiceMissionModals invoiceMission={invoiceMission} parsedSendingType={parsedSendingType} modalState={modalState} />
    </>
  );
};
